import { FC } from 'react'

import {
    Box,
    Checkbox,
    CheckboxGroup,
    Divider,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Link,
    Text,
    useCheckboxGroup,
} from '@chakra-ui/react'
import { Form, Formik } from 'formik'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'

import { useGetThirdStepVerifyMutation, usePostRegisterDataMutation } from '@core/app/api/authApi'
import {
    address,
    allStepsCount,
    cv,
    dateOfBirth,
    email,
    firstName,
    lastName,
    mobilePhone,
    password,
    repeatEmail,
    serviceId,
    stepCount,
    verificationEmailId,
    verificationMobileId,
} from '@core/app/slices/auth'
import { type AuthApiFields, Departments } from '@core/app/types/authApiType'
import { AppButton } from '@core/components/AppButton'
import { useAppSelector } from '@core/utils/hooks/hook'

interface FormValues {
    terms: boolean
    departments: (string | number)[]
}

interface Props {
    departments: Departments
    gender: '0' | '1' | '2' | '3' | '4'
    fields?: AuthApiFields | undefined
    linkedinUrl?: string | null
}

export const DepartmentsChooseStep: FC<Props> = ({ departments, gender, fields, linkedinUrl }) => {
    const stepsCount = useAppSelector(allStepsCount)
    const stepNumber = useAppSelector(stepCount)
    const emailValue = useAppSelector(email)
    const passwordValue = useAppSelector(password)
    const repeatEmailValue = useAppSelector(repeatEmail)
    const firstNameValue = useAppSelector(firstName)
    const lastNameValue = useAppSelector(lastName)
    const mobilePhoneValue = useAppSelector(mobilePhone)
    const addressValue = useAppSelector(address)
    const cvValue = useAppSelector(cv)
    const dateOfBirthValue = useAppSelector(dateOfBirth)
    const emailId = useAppSelector(verificationEmailId)
    const mobileId = useAppSelector(verificationMobileId)
    const serviceIdValue = useAppSelector(serviceId)

    const [verifyData] = useGetThirdStepVerifyMutation()
    const [sendData, { isLoading }] = usePostRegisterDataMutation()

    const { t } = useTranslation()

    const { value, getCheckboxProps } = useCheckboxGroup({
        defaultValue: [],
    })
    const initialValues: FormValues = {
        terms: false,
        departments: value,
    }
    const { executeRecaptcha } = useGoogleReCaptcha()

    const verifyAuthData = async (
        values: FormValues,
        deps: { [key: string]: { departmentId: number } },
        submitted: Function,
        setError: Function
    ) => {
        try {
            const response = await verifyData({
                data: {
                    userAgreement: values.terms,
                    departments: deps,
                },
            }).unwrap()

            if (response.success) {
                registerHandler(values, deps, submitted, setError)
            }
        } catch (error) {
            console.log(error)
        }
    }

    const registerHandler = async (
        values: FormValues,
        deps: { [key: string]: { departmentId: number } },
        submitted: Function,
        setError: Function
    ) => {
        if (!executeRecaptcha) {
            console.error('ReCAPTCHA has not loaded yet')
            return
        }

        const token = await executeRecaptcha('api_register')

        if (token) {
            try {
                const addressData =
                    fields?.address.visible &&
                    ((addressValue.country || '').trim().length > 0 || (addressValue.city || '').trim().length > 0)
                        ? {
                              address1: addressValue.address1,
                              country: addressValue.country,
                              city: addressValue.city,
                              postalCode: addressValue.postalCode,
                          }
                        : undefined

                const response = await sendData({
                    data: {
                        validateMobileId: mobileId !== 0 ? mobileId : undefined,
                        validateEmailId: emailId !== 0 ? emailId : undefined,
                        serviceUid: serviceIdValue ? serviceIdValue : undefined,
                        email: emailValue,
                        repeatEmail: repeatEmailValue,
                        password: passwordValue,
                        firstName: firstNameValue,
                        lastName: lastNameValue,
                        userAgreement: values.terms,
                        // mobilePhone: {
                        //     prefix: mobilePhoneValue.prefix,
                        //     number: mobilePhoneValue.number.toString(),
                        // },
                        mobilePhone:
                            fields?.mobilePhone.visible &&
                            ((mobilePhoneValue.prefix || '').trim().length > 1 ||
                                String(mobilePhoneValue.number || '').trim().length > 0)
                                ? {
                                      prefix: mobilePhoneValue.prefix,
                                      number: mobilePhoneValue.number,
                                  }
                                : undefined,
                        cv:
                            fields?.cv.visible && cvValue
                                ? {
                                      base64: cvValue.base64,
                                      ext: cvValue.ext,
                                      name: cvValue.name,
                                  }
                                : undefined,
                        // address: {
                        //     address1: addressValue.address1,
                        //     country: addressValue.country,
                        //     city: addressValue.city,
                        //     postalCode: addressValue.postalCode,
                        // },
                        address: addressData,
                        dateOfBirth: fields?.dateOfBirth.visible ? dateOfBirthValue : undefined,
                        departments: deps,
                        'g-recaptcha-response': token,
                        gender: fields?.gender.visible && +gender ? +gender : undefined,
                        linkedinUrl: linkedinUrl || null,
                    },
                }).unwrap()

                location.href = response.redirectUrl
                localStorage.setItem('is_logged', 'true')
                localStorage.removeItem('apply_dialog')
                localStorage.removeItem('service_code_auth')
            } catch (error: any) {
                if (error?.status === 422) {
                    const apiMessage = error?.message || 'Invalid Recaptcha. Please, reload the page and try again.'
                    setError('terms', apiMessage)
                } else {
                    console.log(error)
                }
                submitted()
            } finally {
                // submitted()
            }
        }
    }

    const toggleCheckbox = (checked: boolean, id: number, set: Function, values: FormValues) => {
        const array = [...values.departments]
        if (checked) {
            array.push(id)
        } else {
            array.splice(array.indexOf(id), 1)
        }
        set('departments', array)
    }

    return (
        <Flex direction={'column'}>
            <Text fontSize={'xs'} color={'text.thirdly'} mb={1}>
                {t('6593')} {stepNumber} {t('1165')} {stepsCount}
            </Text>
            <Text fontSize={'2xl'} color={'text.main'} fontWeight={'semibold'} mb={6}>
                {t('1478')}
            </Text>
            <Formik
                enableReinitialize={true}
                initialValues={initialValues}
                validationSchema={Yup.object().shape({
                    terms: Yup.bool().oneOf([true], `${t('6616')}`),
                    departments: Yup.array().min(1, `${t('6621')}`),
                })}
                onSubmit={(values, { setSubmitting, setFieldError }) => {
                    const deps: any = new Object()
                    const submitted = () => {
                        setSubmitting(false)
                    }
                    values.departments.map((id) => {
                        deps[id] = {
                            departmentId: id,
                        }
                    })

                    verifyAuthData(values, deps, submitted, setFieldError)
                }}
            >
                {({ values, touched, errors, handleSubmit, setFieldValue, isSubmitting }) => (
                    <Form onSubmit={handleSubmit} style={{ marginBottom: '0' }}>
                        <FormControl isInvalid={!!errors.departments && touched.departments}>
                            <FormLabel>
                                <Text fontSize={'xs'} color={'text.secondary'} mb={4}>
                                    {t('6622')}
                                </Text>
                            </FormLabel>
                            <Box as={'div'} overflowY={'auto'} maxH={'210px'}>
                                <CheckboxGroup>
                                    <Box display={'flex'} flexDirection={'column'} gap={4} alignItems={'start'}>
                                        {Object.values(departments || {}).map((dep) => (
                                            <Checkbox
                                                key={dep.id}
                                                onChange={(e) =>
                                                    toggleCheckbox(e.target.checked, dep.id, setFieldValue, values)
                                                }
                                                _hover={{
                                                    backgroundColor: '#f9f9f9',
                                                }}
                                                _focus={{
                                                    boxShadow: '0 0 0 1px #E0E0e0',
                                                }}
                                                cursor={'pointer'}
                                            >
                                                <Text fontSize={'sm'} color={'text.secondary'}>
                                                    {dep.name}
                                                </Text>
                                            </Checkbox>
                                        ))}
                                    </Box>
                                </CheckboxGroup>
                            </Box>
                            <FormErrorMessage>{errors.departments && errors.departments}</FormErrorMessage>
                        </FormControl>
                        <Divider my={8} />
                        <FormControl isInvalid={!!errors.terms && touched.terms} mb={8}>
                            <Checkbox
                                checked={values.terms}
                                onChange={(e) => {
                                    setFieldValue('terms', e.target.checked)
                                }}
                                alignItems={'start'}
                                _hover={{
                                    backgroundColor: '#f9f9f9',
                                }}
                                _focus={{
                                    boxShadow: '0 0 0 1px #E0E0e0',
                                }}
                                cursor={'pointer'}
                            >
                                <Text fontSize={'xs'}>
                                    {t('6623')}
                                    <Link
                                        fontSize={'xs'}
                                        color={'text.link'}
                                        fontWeight={'medium'}
                                        ml={0.5}
                                        href={'/user-agreement'}
                                        target={'_blank'}
                                    >
                                        {t('4635')}
                                    </Link>
                                    ,{' '}
                                    <Link
                                        fontSize={'xs'}
                                        color={'text.link'}
                                        fontWeight={'medium'}
                                        ml={0.5}
                                        href={'/privacy-policy'}
                                        target={'_blank'}
                                    >
                                        {t('4636')}
                                    </Link>{' '}
                                    {t('2504')}{' '}
                                    <Link
                                        fontSize={'xs'}
                                        color={'text.link'}
                                        fontWeight={'medium'}
                                        ml={0.5}
                                        href={'/cookie-notice'}
                                        target={'_blank'}
                                    >
                                        {t('4637')}
                                    </Link>
                                    .
                                </Text>
                            </Checkbox>
                            <FormErrorMessage>{errors.terms && errors.terms}</FormErrorMessage>
                        </FormControl>
                        <FormControl display={'flex'} justifyContent={'end'}>
                            <AppButton
                                translate="no"
                                title={t('3035')}
                                type={'submit'}
                                variant={'primary'}
                                isLoading={isLoading || isSubmitting}
                                w={'120px'}
                                h={'44px'}
                            />
                        </FormControl>
                    </Form>
                )}
            </Formik>
        </Flex>
    )
}
